import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import "./LegalPage.css";

export default function PrivacyPage() {

  return (
    <div className="legal-page">
      {/* Navigation */}
      <SiteNav />

      {/* Header */}
      <div className="legal-header">
        <div className="legal-header-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-subtitle">
            Last updated: December 5, 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="legal-container">
        <div className="legal-content">
          <section className="legal-section">
            <h2 className="legal-section-title">1) Introduction</h2>
            <p className="legal-text">
              This Privacy Policy explains how OkeyBot (“OkeyBot,” “we,” “us,” or “our”) collects, uses, discloses, and protects information when you use the OkeyBot Discord bot and the okeybot.ai web dashboard (together, the “Service”). By using the Service, you agree to this Policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">2) What OkeyBot does</h2>
            <p className="legal-text">
              OkeyBot lets you use your own API keys to access AI models from providers you choose (e.g., OpenAI, Anthropic, Google, xAI, Groq, Perplexity, OpenRouter) inside Discord. We route your prompts and any relevant attachments to your chosen providers using your keys, and return their outputs to Discord. We also track usage metadata and estimated costs so you can monitor spend.
            </p>
            <p className="legal-text">
              In addition to using your own provider accounts, we may offer OkeyBot‑hosted or “built‑in” models that do not require you to supply a key for a given provider. These built‑in options may use OkeyBot‑managed credits or quotas, and we process data for those models in the same way as for your own keys: to generate replies and provide usage analytics.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">3) Information we collect</h2>

            <h3 className="legal-subsection-title">Account and session</h3>
            <ul className="legal-list">
              <li><strong>Discord OAuth data:</strong> Discord ID, username, and email (if provided by Discord), plus a derived avatar URL.</li>
              <li><strong>Session:</strong> A secure, httpOnly session cookie to keep you logged in to the dashboard.</li>
            </ul>

            <h3 className="legal-subsection-title">Service configuration you choose to store with us</h3>
            <ul className="legal-list">
              <li><strong>API keys:</strong> Your provider keys (one per provider), stored encrypted at rest and decrypted only when needed to fulfill your requests.</li>
              <li><strong>Models and preferences:</strong> Enabled/disabled models, default model preference (“last” or a specific model), per‑model display names and prompts, a global system prompt, participation preference (“Allow others”), and timezone.</li>
            </ul>
            <h3 className="legal-subsection-title">Credits and quotas</h3>
            <ul className="legal-list">
              <li><strong>OkeyBot credits and limits:</strong> Information about your OkeyBot‑managed credits or quotas (for example, balances, grants, and usage against those balances) so we can enforce plan limits and access to any built‑in models you choose to use.</li>
            </ul>
            <h3 className="legal-subsection-title">Usage and analytics (metadata)</h3>
            <ul className="legal-list">
              <li><strong>Usage logs:</strong> Provider name and model ID, request type, consumption metrics (e.g., tokens, compute units, or generation counts), estimated cost, timestamps, and thread IDs. We do not store the text of your prompts or responses in our database.</li>
            </ul>
            <h3 className="legal-subsection-title">Security and abuse prevention</h3>
            <ul className="legal-list">
              <li><strong>Permanent identifiers:</strong> We retain your Discord ID permanently to prevent fraud, enforce bans, and ensure one-time offers (such as welcome credits) are not claimed multiple times by the same person.</li>
            </ul>
            <h3 className="legal-subsection-title">Discord message content</h3>
            <ul className="legal-list">
              <li><strong>Threads:</strong> In bot‑managed threads, we read message content and attachments to build prompts/context and generate replies. We do not store message bodies in our database. Content persists in Discord per Discord’s policies.</li>
              <li><strong>Mentions/DMs:</strong> Outside bot‑managed threads, we use only the reply chain for context when generating a response.</li>
            </ul>

            <h3 className="legal-subsection-title">Billing (Stripe)</h3>
            <ul className="legal-list">
              <li><strong>Subscription metadata:</strong> Plan, status, Stripe customer ID, Stripe subscription ID, and current period end. Stripe processes payments; we do not store full payment card numbers.</li>
            </ul>

            <h3 className="legal-subsection-title">Technical logs</h3>
            <ul className="legal-list">
              <li><strong>Server logs:</strong> Standard operational logs for security and reliability (e.g., timestamps, IP address, user agent). We configure logs to avoid recording conversation content or provider payloads.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">4) How we use information</h2>
            <ul className="legal-list">
              <li>Provide, operate, and secure the Service (including creating/managing Discord threads, generating replies, and enforcing your thread‑owner preferences).</li>
              <li>Use your encrypted API keys to call the providers/models you enable.</li>
              <li>Track usage and estimated cost and present analytics to you.</li>
              <li>Prevent abuse and fraud, troubleshoot issues, and improve reliability.</li>
              <li>Handle billing and subscriptions through Stripe and respond to support inquiries.</li>
              <li>Comply with applicable law and enforce our Terms.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">5) What we don’t do</h2>
            <ul className="legal-list">
              <li>We do not sell personal information.</li>
              <li>We do not store or log Discord messages or attachments in our database. We process these transiently to provide the Service and configure logging to avoid recording prompts or outputs.</li>
              <li>We do not train any models on your data.</li>
              <li>We do not share your API keys with other users or with parties other than your chosen AI providers and our infrastructure subprocessors.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">6) Cookies</h2>
            <p className="legal-text">
              We use a single session cookie that is httpOnly, Secure, and SameSite=None to keep you signed in to the dashboard. We do not set third‑party advertising cookies. If we add analytics or additional cookies in the future, we will update this Policy and, where required, request consent.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">7) Providers and third parties</h2>

            <h3 className="legal-subsection-title">AI providers you enable</h3>
            <p className="legal-text">
              The AI providers you choose receive the content necessary to fulfill your requests and may process/retain it under their own terms and policies. You should review and configure provider‑side retention and training settings in your provider accounts.
            </p>
            <ul className="legal-list">
              <li>OpenAI: <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">https://openai.com/policies/privacy-policy</a></li>
              <li>Anthropic: <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">https://www.anthropic.com/privacy</a></li>
              <li>Google: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
              <li>xAI: <a href="https://x.ai/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://x.ai/legal/privacy-policy</a></li>
              <li>Groq: <a href="https://groq.com/privacy-policy" target="_blank" rel="noopener noreferrer">https://groq.com/privacy-policy</a></li>
              <li>Perplexity: <a href="https://www.perplexity.ai/privacy" target="_blank" rel="noopener noreferrer">https://www.perplexity.ai/privacy</a></li>
              <li>OpenRouter: <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer">https://openrouter.ai/privacy</a></li>
            </ul>

            <h3 className="legal-subsection-title">Other service providers</h3>
            <ul className="legal-list">
              <li><strong>Discord:</strong> Platform for messages and media; Discord stores your messages under its own terms and policies.</li>
              <li><strong>Stripe:</strong> Payments and subscriptions processing.</li>
              <li><strong>Hosting/monitoring:</strong> Infrastructure providers we use to operate the Service.</li>
            </ul>
            <p className="legal-text">
              We may also disclose information as required by law, to protect rights and safety, or in connection with a merger, acquisition, or asset sale. We may publish or provide a current subprocessor list upon request. We do not sell or share personal information for cross‑context behavioral advertising.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">8) Data security</h2>
            <ul className="legal-list">
              <li><strong>Encryption at rest:</strong> Your API keys are stored encrypted and protected by access controls.</li>
              <li><strong>Encryption in transit:</strong> We use TLS/HTTPS for data in transit.</li>
              <li><strong>Access controls and rate‑limiting:</strong> Technical and organizational measures to help protect the Service.</li>
            </ul>
            <p className="legal-text">
              No method of transmission or storage is 100% secure. If we become aware of a security incident affecting your information, we will take steps consistent with applicable law.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">9) Data retention</h2>
            <ul className="legal-list">
              <li><strong>Account and preferences:</strong> Generally kept while your account is active. If you delete your account, we remove your user profile, API keys, and preferences.</li>
              <li><strong>Usage metadata:</strong> Retained to provide analytics and support. We may retain aggregated, de‑identified statistics after deletion.</li>
              <li><strong>API keys:</strong> Deleted when you remove them or delete your account.</li>
              <li><strong>Anti-abuse records:</strong> We permanently retain your Discord ID even after account deletion. This is strictly necessary to prevent abuse of free tiers and one-time grants.</li>
              <li><strong>Credits and quotas:</strong> Kept while your account is active to operate OkeyBot‑managed credits or limits (such as built‑in model access) and then deleted or aggregated once no longer needed for the purposes described in this Policy.</li>
              <li><strong>Billing records:</strong> Certain billing/subscription records may be retained as required by law.</li>
              <li><strong>Discord content:</strong> Messages and media remain on Discord; deleting your account with us does not remove content from Discord.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">10) Your choices and rights</h2>

            <h3 className="legal-subsection-title">Controls in Discord</h3>
            <ul className="legal-list">
              <li>If “Allow others” is disabled, only you can trigger your key, change models, or view thread usage via /usage in your threads.</li>
              <li>You can request regeneration of responses in threads you own by editing your message and confirming regeneration.</li>
            </ul>

            <h3 className="legal-subsection-title">Controls in the dashboard</h3>
            <ul className="legal-list">
              <li>Add/remove API keys; enable/disable models; set defaults; edit per‑model and global prompts; toggle “Allow others”; set timezone.</li>
            </ul>

            <h3 className="legal-subsection-title">Access, correction, deletion</h3>
            <ul className="legal-list">
              <li>View/update your stored preferences in the dashboard.</li>
              <li>Delete your account in the dashboard to remove your API keys, preferences, user models, usage logs, thread settings, and user record from our systems. We may retain limited billing information as permitted or required by law.</li>
              <li>Manage provider‑side retention/training settings directly in your provider accounts.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">11) California privacy (CCPA/CPRA)</h2>
            <p className="legal-text">
              We do not sell or share personal information for cross‑context behavioral advertising. Subject to exceptions, California residents can request access, deletion, and correction of personal information. To exercise your rights, contact us as described below. We will not discriminate against you for exercising your rights.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">12) EEA/UK privacy (GDPR)</h2>
            <ul className="legal-list">
              <li><strong>Controller role:</strong> We are a controller for account, billing, and usage metadata. For prompts and attachments you instruct us to send to your chosen providers, we process them to provide the Service you request.</li>
              <li><strong>Legal bases:</strong> Performance of a contract (to provide the Service), our legitimate interests (security, abuse prevention, analytics), compliance with legal obligations, and consent where applicable.</li>
              <li><strong>Transfers:</strong> Your information may be processed outside your country. Where required, we rely on appropriate safeguards (e.g., standard contractual clauses).</li>
              <li><strong>Your rights:</strong> You may have rights of access, rectification, erasure, restriction, objection, and data portability, subject to limits in law. Contact us as described below.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">13) Children’s privacy</h2>
            <p className="legal-text">
              The Service is not intended for children under 13 (or the minimum age required in your jurisdiction to use Discord). We do not knowingly collect personal information from children. If you believe a child has provided information to us, contact us to request deletion.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">14) International use</h2>
            <p className="legal-text">
              We may process and store information in any country where we or our providers operate. By using the Service, you consent to such transfers.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">15) Changes to this Policy</h2>
            <p className="legal-text">
              We may update this Policy from time to time. The updated version will be posted here with a new “Last updated” date. If changes are material, we may provide additional notice (e.g., in the dashboard or via Discord).
            </p>
          </section>

          <section className="legal-section">
            <h2 className="legal-section-title">16) Contact us</h2>
            <p className="legal-text">If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <div className="legal-contact-card">
              <div className="legal-contact-item"><strong>Contact:</strong> contact@okeybot.ai</div>
              <div className="legal-contact-item">
                <strong>Discord:</strong> Join our support server
              </div>
            </div>
          </section>
          <section className="legal-section">
            <h2 className="legal-section-title">17) Your responsibilities</h2>
            <ul className="legal-list">
              <li><strong>Provider choices:</strong> You decide which providers/models to enable. Their processing is governed by their own terms and policies.</li>
              <li><strong>Provider retention/training:</strong> Configure retention or training preferences in your provider accounts as needed for your data.</li>
              <li><strong>Costs:</strong> You are responsible for any fees charged by your chosen providers. OkeyBot does not mark up provider pricing.</li>
              <li><strong>Sensitive content:</strong> Do not post secrets or regulated personal data in Discord threads. Treat Discord as persistent.</li>
              <li><strong>Discord policies:</strong> Your use of Discord is subject to Discord’s terms and community guidelines.</li>
            </ul>
          </section>

        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
